import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { isEqual, isFunction } from "lodash";
import { initialFilter } from "./CustomerUIHelpers";

// REDUX
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { getListCustomer } from "../../redux/customer";

// CONTEXT
import useAuth from "../../hooks/useAuth";
import { AESEncryptURL } from "utils/encryption/AESEncrypt";

const CustomerUIContext = createContext();

export const useCustomerUIContext = () => {
  return useContext(CustomerUIContext);
};

export const CustomerUIConsumer = CustomerUIContext.Consumer;

export function CustomerUIProvider({ children }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { authData } = useAuth();
  const { getListCustomerData } = useSelector(
    (state) => ({
      getListCustomerData: state.GetListCustomerData,
    }),
    shallowEqual
  );
  const [queryParams, setQueryParamsBase] = useState(initialFilter);

  useEffect(() => {
    dispatch(getListCustomer(queryParams));
  }, [dispatch, queryParams]);

  const setQueryParams = useCallback((nextQueryParams) => {
    setQueryParamsBase((prevQueryParams) => {
      if (isFunction(nextQueryParams)) {
        nextQueryParams = nextQueryParams(prevQueryParams);
      }
      if (isEqual(prevQueryParams, nextQueryParams)) {
        return prevQueryParams;
      }
      return nextQueryParams;
    });
  }, []);

  const dataUIEvents = {
    goToCreateCustomer: () => {
      navigate(`/customer/create`, {
        state: {
          showDialog: true,
        },
      });
    },
    goToExportCustomer: () => {
      navigate(`/customer/export`, {
        state: {
          showDialog: true,
        },
      });
    },
    goToCustomerDetail: (id) => {
      const secureID = AESEncryptURL(id.toString());
      navigate(`/customer/${secureID}/detail`, {
        state: {
          showDialog: true,
        },
      });
    },
    goToCustomerEdit: (id) => {
      const secureID = AESEncryptURL(id.toString());
      navigate(`/customer/${secureID}/edit`, {
        state: {
          showDialog: true,
        },
      });
    },
    goToDeleteCustomer: (id, data) => {
      const secureID = AESEncryptURL(id.toString());
      navigate(`/customer/${secureID}/delete`, {
        state: {
          showDialog: true,
          customerData: data,
        },
      });
    },
    goToSchedule: (paramsSelectedDate) => {
      navigate("/schedule", {
        state: {
          paramsSelectedDate: paramsSelectedDate,
        },
      });
    },
    goToCustomer: () => {
      navigate("/customer");
    },
    onHide: () => {
      navigate(-1);
    },
  };

  const handleResetData = () => {
    setQueryParamsBase(initialFilter);
    dispatch(getListCustomer(queryParams));
  };

  const value = {
    authData,
    queryParams,
    getListCustomerData,
    setQueryParams,
    setQueryParamsBase,
    handleResetData,
    goToCreateCustomer: dataUIEvents.goToCreateCustomer,
    goToExportCustomer: dataUIEvents.goToExportCustomer,
    goToCustomerDetail: dataUIEvents.goToCustomerDetail,
    goToCustomerEdit: dataUIEvents.goToCustomerEdit,
    goToDeleteCustomer: dataUIEvents.goToDeleteCustomer,
    goToSchedule: dataUIEvents.goToSchedule,
    goToCustomer: dataUIEvents.goToCustomer,
    onHide: dataUIEvents.onHide,
  };

  return (
    <CustomerUIContext.Provider value={value}>
      {children}
    </CustomerUIContext.Provider>
  );
}
